/**
 * SubscriptionCard Component
 *
 * Displays subscription information and usage limits for workers
 */

'use client';

import { FC, useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useRouter, usePathname } from 'next/navigation';
import { Typography } from '@/src/shared/ui/typography/Typography';
import { PrimaryButton, SecondaryButton } from '@/src/shared/ui/button';
import { Crown, TrendingUp, AlertCircle, CreditCard } from 'lucide-react';
import { subscriptionsApi, SubscriptionUsage } from '@/src/shared/api/subscriptions.api';
import { SubscriptionPlans } from './SubscriptionPlans';
import { toast } from 'sonner';

export const SubscriptionCard: FC = () => {
  const t = useTranslations('profile.subscription');
  const router = useRouter();
  const pathname = usePathname();
  const locale = pathname.split('/')[1];

  const [usage, setUsage] = useState<SubscriptionUsage | null>(null);
  const [loading, setLoading] = useState(true);
  const [managingSubscription, setManagingSubscription] = useState(false);
  const [showPlans, setShowPlans] = useState(false);

  useEffect(() => {
    loadSubscriptionUsage();
  }, []);

  const loadSubscriptionUsage = async () => {
    try {
      setLoading(true);
      const data = await subscriptionsApi.getMySubscriptionUsage();
      setUsage(data);
    } catch (error) {
      console.error('Failed to load subscription usage:', error);
      toast.error(t('errors.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return 'text-gray-600';
      case 'STANDARD':
        return 'text-blue-600';
      case 'PROFESSIONAL':
        return 'text-purple-600';
      case 'ADVANCED':
        return 'text-amber-600';
      default:
        return 'text-gray-600';
    }
  };

  const getTierBgColor = (tier: string) => {
    switch (tier) {
      case 'FREE':
        return 'bg-gray-100';
      case 'STANDARD':
        return 'bg-blue-100';
      case 'PROFESSIONAL':
        return 'bg-purple-100';
      case 'ADVANCED':
        return 'bg-amber-100';
      default:
        return 'bg-gray-100';
    }
  };

  const getUsagePercentage = (current: number, max: number) => {
    if (max >= 9999) return 0; // Unlimited
    return Math.min((current / max) * 100, 100);
  };

  const isLimitReached = (current: number, max: number) => {
    return max < 9999 && current >= max;
  };

  const handleUpgrade = () => {
    router.push(`/${locale}/subscription/pricing`);
  };

  const handleManageSubscription = async () => {
    try {
      setManagingSubscription(true);
      const { url } = await subscriptionsApi.getCustomerPortalUrl(
        `${window.location.origin}/${locale}/profile?tab=settings`,
      );
      window.location.href = url;
    } catch (error) {
      console.error('Failed to open customer portal:', error);
      toast.error(t('errors.portalFailed'));
      setManagingSubscription(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 border border-border-primary rounded-lg animate-pulse">
        <div className="h-6 bg-background-secondary rounded w-1/3 mb-4"></div>
        <div className="h-4 bg-background-secondary rounded w-2/3 mb-2"></div>
        <div className="h-4 bg-background-secondary rounded w-1/2"></div>
      </div>
    );
  }

  if (!usage) {
    return null;
  }

  const { tier, limits, usage: currentUsage, subscription } = usage;
  const hasStripeCustomer = subscription.id !== null;

  // Show plans view if user clicked "View Plans"
  if (showPlans) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <Typography variant="h3">{t('title')}</Typography>
          <SecondaryButton onClick={() => setShowPlans(false)}>
            {t('backToOverview')}
          </SecondaryButton>
        </div>
        <SubscriptionPlans
          currentTier={tier}
          currentStatus={subscription.status}
          hasStripeCustomer={hasStripeCustomer}
        />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Plan Card */}
      <div className="p-6 border border-border-primary rounded-lg">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${getTierBgColor(tier)}`}>
              <Crown className={`w-6 h-6 ${getTierColor(tier)}`} />
            </div>
            <div>
              <Typography variant="h3" className={getTierColor(tier)}>
                {t(`tiers.${tier}.name`)}
              </Typography>
              <Typography variant="bodySm" className="text-text-secondary">
                {t(`tiers.${tier}.description`)}
              </Typography>
            </div>
          </div>
          <div className="flex gap-2">
            {tier !== 'ADVANCED' && (
              <PrimaryButton className="flex items-center gap-2" onClick={() => setShowPlans(true)}>
                <TrendingUp className="w-4 h-4" />
                {t('viewPlans')}
              </PrimaryButton>
            )}
          </div>
        </div>

        {/* Usage Stats */}
        <div className="space-y-4">
          <Typography variant="body" className="font-semibold">
            {t('usage.title')}
          </Typography>

          {/* Proposals */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Typography variant="bodySm" className="text-text-secondary">
                {t('usage.proposals')}
              </Typography>
              <Typography
                variant="bodySm"
                className={`font-semibold ${
                  isLimitReached(currentUsage.activeProposals, limits.maxProposals)
                    ? 'text-error-primary'
                    : 'text-text-primary'
                }`}
              >
                {currentUsage.activeProposals} / {limits.maxProposals >= 9999 ? '∞' : limits.maxProposals}
              </Typography>
            </div>
            <div className="w-full bg-background-secondary rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  isLimitReached(currentUsage.activeProposals, limits.maxProposals)
                    ? 'bg-error-primary'
                    : 'bg-accent-primary'
                }`}
                style={{
                  width: `${getUsagePercentage(currentUsage.activeProposals, limits.maxProposals)}%`,
                }}
              />
            </div>
            {isLimitReached(currentUsage.activeProposals, limits.maxProposals) && (
              <div className="flex items-center gap-2 mt-2 text-error-primary">
                <AlertCircle className="w-4 h-4" />
                <Typography variant="bodySm">{t('usage.limitReached')}</Typography>
              </div>
            )}
          </div>

          {/* Categories */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Typography variant="bodySm" className="text-text-secondary">
                {t('usage.categories')}
              </Typography>
              <Typography
                variant="bodySm"
                className={`font-semibold ${
                  isLimitReached(currentUsage.categories, limits.maxCategories)
                    ? 'text-error-primary'
                    : 'text-text-primary'
                }`}
              >
                {currentUsage.categories} / {limits.maxCategories >= 9999 ? '∞' : limits.maxCategories}
              </Typography>
            </div>
            <div className="w-full bg-background-secondary rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  isLimitReached(currentUsage.categories, limits.maxCategories)
                    ? 'bg-error-primary'
                    : 'bg-accent-primary'
                }`}
                style={{
                  width: `${getUsagePercentage(currentUsage.categories, limits.maxCategories)}%`,
                }}
              />
            </div>
          </div>

          {/* Portfolio Items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Typography variant="bodySm" className="text-text-secondary">
                {t('usage.portfolio')}
              </Typography>
              <Typography
                variant="bodySm"
                className={`font-semibold ${
                  isLimitReached(currentUsage.portfolioItems, limits.maxPortfolioItems)
                    ? 'text-error-primary'
                    : 'text-text-primary'
                }`}
              >
                {currentUsage.portfolioItems} /{' '}
                {limits.maxPortfolioItems >= 9999 ? '∞' : limits.maxPortfolioItems}
              </Typography>
            </div>
            <div className="w-full bg-background-secondary rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  isLimitReached(currentUsage.portfolioItems, limits.maxPortfolioItems)
                    ? 'bg-error-primary'
                    : 'bg-accent-primary'
                }`}
                style={{
                  width: `${getUsagePercentage(currentUsage.portfolioItems, limits.maxPortfolioItems)}%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* Manage Subscription */}
        {hasStripeCustomer && tier !== 'FREE' && (
          <div className="mt-6 pt-6 border-t border-border-primary">
            <SecondaryButton
              className="w-full flex items-center justify-center gap-2"
              onClick={handleManageSubscription}
              disabled={managingSubscription}
            >
              {managingSubscription ? (
                <>
                  <span className="animate-spin mr-2">⏳</span>
                  {t('loading')}
                </>
              ) : (
                <>
                  <CreditCard className="w-4 h-4" />
                  {t('manageSubscription')}
                </>
              )}
            </SecondaryButton>
            <Typography variant="bodySm" className="text-text-secondary text-center mt-2">
              {t('manageSubscriptionHint')}
            </Typography>
          </div>
        )}
      </div>
    </div>
  );
};
